import React, { useState, useEffect } from 'react';
import FieldGroup from '../../Fields/FieldGroup';
import InputSelectField from '../../Fields/InputSelectField';
import PaymentApplicationMonthsTable from '../../Fields/PaymentApplicationMonthsTable';
import SettingsPaymentTypes from '../../Fields/SettingsPaymentTypes';
import SettingsAccountingMethod from '../../Fields/SettingsAccountingMethod'
import './SettingsPaymentApplicationPanel.scss';

import featureFlags from '../../../featureFlags';

const SettingsPaymentApplicationsPanel = (props) => {
    const [priorMonthApplication, setPriorMonthApplication] = useState("MANUAL");
    const [futureMonthApplication, setFutureMonthApplication] = useState("NOT_ALLOWED");
    const [monthCloseDay, setMonthCloseDay] = useState("ONE");
    const [monthCloseDayType, setMonthCloseDayType] = useState("CALENDAR");
    const [adminProcessPayment, setAdminProcessPayment] = useState("DO_NOT_DISPLAY");
    const [manageRecurringPayment, setManageRecurringPayment] = useState("DO_NOT_DISPLAY");
    const [editPaymentOptions, setEditPaymentOptions] = useState("DO_NOT_DISPLAY");
    const [accountingMethod, setAccountingMethod] = useState("");
    const [initialized, setInitialized] = useState(false);

    const initiateValues = () => {
        setPriorMonthApplication(props.settings.fields.prior_month_application.value);
        setFutureMonthApplication(props.settings.fields.future_month_application.value);
        setMonthCloseDay(props.settings.fields.prior_month_application.value === "MANUAL" ? null : props.settings.fields.month_close_day.value);
        setMonthCloseDayType(props.settings.fields.month_close_day_type.value);
        setAdminProcessPayment(props.settings.fields.admin_process_payments.value);
        setManageRecurringPayment(props.settings.fields.manage_recurring_payments.value);
        setEditPaymentOptions(props.settings.fields.edit_payment_options.value);
        setAccountingMethod(props.settings.fields.accounting_method.value);
    };

    useEffect(() => {
        initiateValues();
        setInitialized(true);
    }, []);

    useEffect(() => {
        if (!props.editMode) {
            initiateValues();
        }
    }, [props.editMode]);

    useEffect(() => {
        props.storePaymentApplicationSettingsData(
            priorMonthApplication,
            futureMonthApplication,
            monthCloseDay,
            monthCloseDayType,
            adminProcessPayment,
            manageRecurringPayment,
            editPaymentOptions,
            accountingMethod
        );

        if (priorMonthApplication === "SCHEDULED" && monthCloseDay == null) {
            setMonthCloseDay("ONE");
        }
    }, [
        priorMonthApplication,
        futureMonthApplication,
        monthCloseDay,
        monthCloseDayType,
        adminProcessPayment,
        manageRecurringPayment,
        editPaymentOptions,
        accountingMethod
    ]);

    const { editMode, fieldConfiguration } = props;

    const renderBillingDayField = (priorMonthApplication === 'SCHEDULED');
    const priorMonthApplicationOptions = fieldConfiguration.facilitySettings.prior_month_application.options;
    const futureMonthApplicationOptions = fieldConfiguration.facilitySettings.future_month_application.options;
    const monthDayOptions = fieldConfiguration.facilitySettings.month_close_day.options;
    const monthDayTypeOptions = fieldConfiguration.facilitySettings.month_close_day_type.options;
    const maxMonthDayOptions = monthCloseDayType === 'BUSINESS' ? 10 : monthDayOptions.length;
    const adminProcessPaymentOptions = fieldConfiguration.facilitySettings.admin_process_payments.options;

    return (
        <>
            <FieldGroup label="Payment Types">
                <SettingsPaymentTypes
                    facilityPaymentTypes={props.facilityPaymentTypes}
                    organizationId={props.settings.fields.organization_id.value}
                    facilityUid={props.settings.fields.facility_id.value}
                    onPaymentTypesChange={props.onUpdatePaymentTypes}
                    editMode={editMode}
                />
            </FieldGroup>
            <FieldGroup label="Payment Application">
                <SettingsAccountingMethod
                    facilityUid={props.settings.fields.facility_id.value}
                    accountingMethod={accountingMethod}
                    fieldConfiguration={fieldConfiguration}
                    initialized={initialized}
                    onChange={setAccountingMethod}
                    editMode={editMode}>
                </SettingsAccountingMethod>
                <div className='parkchirp-payment-application-row'>
                    <div className='parkchirp-payment-application-row-label'>
                        Prior Month Application:
                    </div>
                    <div className='parkchirp-payment-application-row-field'>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <InputSelectField
                                inputId="parkchirp-payment-application--prior-month-application"
                                value={priorMonthApplication}
                                options={priorMonthApplicationOptions}
                                editMode={editMode}
                                onChange={setPriorMonthApplication}
                            />

                            {(renderBillingDayField) &&
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <span style={{ marginLeft: '0.3em', marginRight: '0.3em' }}>to close on the</span>
                                    <InputSelectField inputId="parkchirp--settings--billing-day"
                                        value={monthCloseDay}
                                        options={monthDayOptions.slice(0, maxMonthDayOptions)}
                                        onChange={setMonthCloseDay}
                                        editMode={editMode}
                                    />
                                    {featureFlags.BUSINESSDAYS &&
                                        <>
                                            <span>&nbsp;</span>
                                            <InputSelectField inputId="parkchirp--settings--month-close-day-type"
                                                value={monthCloseDayType}
                                                options={monthDayTypeOptions}
                                                onChange={setMonthCloseDayType}
                                                editMode={editMode}
                                            />
                                        </>
                                    }
                                    <span style={{ marginLeft: '0.3em' }}>day of each month</span>
                                </div>
                            }
                        </div>
                    </div>
                </div>

                <PaymentApplicationMonthsTable
                    {...props}
                />
                <div className='parkchirp-payment-application-row'>
                    <div className='parkchirp-payment-application-row-label'>
                        Future Application:
                    </div>
                    <div className='parkchirp-payment-application-row-field'>
                        <InputSelectField
                            inputId="parkchirp-payment-application--future-month-application"
                            value={futureMonthApplication}
                            options={futureMonthApplicationOptions}
                            editMode={editMode}
                            onChange={setFutureMonthApplication}
                        />
                    </div>
                </div>
            </FieldGroup>
            <FieldGroup label="Managing & Processing Payments">
                <div className='parkchirp-process-payment-row'>
                    <div className='parkchirp-process-payment-row-label'>
                        Allow Admin to Process Payments
                    </div>
                    <div className='parkchirp-process-payment-row-field'>
                        <InputSelectField
                            inputId="parkchirp-process-payment-id"
                            value={adminProcessPayment}
                            options={adminProcessPaymentOptions}
                            editMode={editMode}
                            onChange={setAdminProcessPayment}
                        />
                    </div>
                </div>
                <div className='parkchirp-process-payment-row'>
                    <div className='parkchirp-process-payment-row-label'>
                        Allow Admin to Configure Recurring Payments
                    </div>
                    <div className='parkchirp-process-payment-row-field'>
                        <InputSelectField
                            inputId="parkchirp-process-payment-id"
                            value={manageRecurringPayment}
                            options={adminProcessPaymentOptions}
                            editMode={editMode}
                            onChange={setManageRecurringPayment}
                        />
                    </div>
                </div>
                <div className='parkchirp-process-payment-row'>
                    <div className='parkchirp-process-payment-row-label'>
                        Allow Admin to Edit Payment Options
                    </div>
                    <div className='parkchirp-process-payment-row-field'>
                        <InputSelectField
                            inputId="parkchirp-process-payment-id"
                            value={editPaymentOptions}
                            options={adminProcessPaymentOptions}
                            editMode={editMode}
                            onChange={setEditPaymentOptions}
                        />
                    </div>
                </div>
            </FieldGroup>
        </>
    );
}

export default SettingsPaymentApplicationsPanel;